import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { requestSignupOTP, verifySignupOTP } from "../api/client";
import { useAuth } from "../context/AuthContext";
import BackgroundAnimation from "../components/BackgroundAnimation";
import "../auth.css";

export default function Signup() {
  const { saveAuth } = useAuth();
  const navigate     = useNavigate();
  const [form, setForm]       = useState({ name:"", email:"", password:"", confirm:"" });
  const [otp, setOtp]         = useState("");
  const [step, setStep]       = useState(1); // 1: Enter details, 2: Enter OTP
  const [error, setError]     = useState("");
  const [info, setInfo]       = useState("");
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    let t;
    if (resendTimer > 0) {
      t = setTimeout(() => setResendTimer(p => p - 1), 1000);
    }
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleRequestOTP = async () => {
    if (!form.name || !form.email || !form.password) { setError("All fields required"); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match"); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    
    setLoading(true); setError(""); setInfo("");
    try {
      await requestSignupOTP({ name: form.name, email: form.email, password: form.password });
      setStep(2);
      setResendTimer(60);
      setInfo("A 6-digit verification code has been sent to your email.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send verification email. Please check your inputs.");
    } finally { setLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) { setError("Please enter a valid 6-digit OTP code."); return; }
    setLoading(true); setError(""); setInfo("");
    try {
      const data = await verifySignupOTP({ email: form.email, otp });
      saveAuth(data);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired OTP. Please try again.");
    } finally { setLoading(false); }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setLoading(true); setError(""); setInfo("");
    try {
      await requestSignupOTP({ name: form.name, email: form.email, password: form.password });
      setResendTimer(60);
      setInfo("A new verification code has been sent to your email.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to resend verification OTP.");
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <BackgroundAnimation />
      <div className="auth-card">
        <div className="auth-brand">
          PINE<span className="accent">CONNECTOR</span>
        </div>
        <div className="auth-title">
          {step === 1 ? "Create account" : "Verify Email"}
        </div>
        <div className="auth-sub">
          {step === 1 
            ? "Enter your details to request verification OTP" 
            : `Verify code sent to: ${form.email}`
          }
        </div>

        {error && (
          <div className="auth-error">{error}</div>
        )}
        
        {info && (
          <div className="auth-info" style={{ color: "var(--green)", background: "rgba(16, 185, 129, 0.08)", border: "1px solid rgba(16, 185, 129, 0.2)", padding: "10px", borderRadius: "var(--radius)", fontSize: "13px", marginBottom: "16px", fontWeight: "500", textAlign: "center" }}>
            {info}
          </div>
        )}

        {step === 1 ? (
          <>
            {[
              {key:"name",     label:"Full Name",       type:"text",     ph:"John Doe"},
              {key:"email",    label:"Email",            type:"email",    ph:"you@example.com"},
              {key:"password", label:"Password",         type:"password", ph:"Min 6 characters"},
              {key:"confirm",  label:"Confirm Password", type:"password", ph:"Repeat password"},
            ].map(({key,label,type,ph}) => (
              <div key={key} className="form-group">
                <label className="form-label">{label}</label>
                <input
                  type={type}
                  className="form-input"
                  placeholder={ph}
                  value={form[key]}
                  onChange={e => setForm(p => ({...p, [key]: e.target.value}))}
                  onKeyDown={e => e.key === "Enter" && handleRequestOTP()}
                />
              </div>
            ))}

            <button
              className="btn btn-primary"
              style={{ width: "100%", marginTop: 8 }}
              onClick={handleRequestOTP}
              disabled={loading}
            >
              {loading ? "Requesting OTP..." : "Request Verification Code"}
            </button>
          </>
        ) : (
          <>
            <div className="form-group">
              <label className="form-label">6-Digit Verification Code</label>
              <input
                type="text"
                className="form-input"
                placeholder="123456"
                maxLength={6}
                style={{ textAlign: "center", fontSize: "18px", letterSpacing: "8px", fontFamily: "var(--mono)" }}
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
                onKeyDown={e => e.key === "Enter" && handleVerifyOTP()}
              />
            </div>

            <button
              className="btn btn-primary"
              style={{ width: "100%", marginTop: 8 }}
              onClick={handleVerifyOTP}
              disabled={loading}
            >
              {loading ? "Verifying..." : "Verify & Complete Signup"}
            </button>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "16px", fontSize: "13px" }}>
              <button 
                className="btn btn-ghost" 
                style={{ padding: "6px 12px", minHeight: "auto" }}
                onClick={() => setStep(1)} 
                disabled={loading}
              >
                ← Edit Details
              </button>
              
              <button 
                className="btn btn-ghost" 
                style={{ padding: "6px 12px", minHeight: "auto", color: resendTimer > 0 ? "var(--text2)" : "var(--accent)" }}
                onClick={handleResendOTP} 
                disabled={loading || resendTimer > 0}
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend Code"}
              </button>
            </div>
          </>
        )}

        <div className="auth-footer" style={{ marginTop: "24px" }}>
          Already have an account?{" "}
          <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
